import isEqual from 'lodash/isequal';
import sortBy from 'lodash/sortby';
import isNumber from 'lodash/isnumber';
import isString from 'lodash/isstring';
import isBoolean from 'lodash/isboolean';
import isNil from 'lodash/isnil';
import isDate from 'lodash/isdate';
import {
  CodeItem, SafetyLimit, SubstanceConversionFactor, SubstanceUomSetValue, UomConversionWire
} from 'app/area/codetable/item/types-store';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { TranslatedState } from 'app/types-shared';
import { CodetableAdministrationState } from 'app/area/shared/codetable/item/types';
import { deepEquals } from 'app/infrastructure/js';
import { CodeComplexValue } from 'app/area/shared/codetable/types';

const comparableByDescription: string[] = ['description', 'longDescription'];
const uomFactorPropertyName = 'factor';

const isPrimitive = (value: any) => isNil(value) || isNumber(value) || isBoolean(value) || isString(value) || typeof value === 'symbol';
const isWithUomSet = (value: any) => value?.every(item => item?.uomSet?.value);
const isWithSubstanceConversionFactor = (value: any) => value?.every(item => item?.uomConversionType?.value);
const isWithUomConversionFactor = (value: any) => value?.every(item => item?.value && item.hasOwnProperty(uomFactorPropertyName));
const isWithSafetyLimit = (value: any) => Array.isArray(value) && value.every(item => item?.application !== undefined);

const shouldCompareShallowly = (source: any, compared: any) =>
  source === undefined && compared === undefined ||
  source === undefined && compared !== undefined && isPrimitive(compared) ||
  source !== undefined && isPrimitive(source) && compared === undefined ||
  source !== undefined && compared !== undefined && isPrimitive(source) && isPrimitive(compared);

const shouldCompareDeeply = (source: CodeItemPropertyValue, compared: CodeItemPropertyValue, property: keyof CodeItem) =>
  isDate(source) || isDate(compared);

const isDefinedPropertyMissing = (source: CodeItem, compared: CodeItem, property: keyof CodeItem): boolean =>
  !compared.hasOwnProperty(property) && source[property] !== undefined;

const isUndefinedPropertyMissing = (source: CodeItem, compared: CodeItem, property: keyof CodeItem) =>
  !compared.hasOwnProperty(property) &&
  (source[property] === undefined || (Array.isArray(source[property]) && (source[property] as []).length === 0));

const addDefinedPropertyToDiff = (
  source: CodeItem,
  compared: CodeItem,
  property: keyof CodeItem,
  diff: (keyof CodeItem)[]
): (keyof CodeItem)[] => isDefinedPropertyMissing(source, compared, property) ? [...diff, property] : diff;

const removePropertyFromDiff = (diff: (keyof CodeItem)[], property: keyof CodeItem): (keyof CodeItem)[] => {
  const propertyInDiffIndex = diff.indexOf(property);
  const isPropertyInDiff = propertyInDiffIndex > -1;

  return isPropertyInDiff ? [...diff.slice(0, propertyInDiffIndex), ...diff.slice(propertyInDiffIndex + 1, diff.length)] : diff;
};

const removeUndefinedProperty = (
  source: CodeItem,
  compared: CodeItem,
  property: keyof CodeItem,
  diff: (keyof CodeItem)[]
): (keyof CodeItem)[] => isUndefinedPropertyMissing(source, compared, property) ? removePropertyFromDiff(diff, property) : diff;

const removeUndefinedProperties = (source: CodeItem, compared: CodeItem): (keyof CodeItem)[] =>
  Object.keys(compared).reduce<(keyof CodeItem)[]>((diff, property) =>
    removeUndefinedProperty(compared, source, property as keyof CodeItem, diff),
    Object.keys(compared) as (keyof CodeItem)[]);

type CodeItemPropertyValue = string | number | CodeComplexValue<number> | CodeComplexValue<number>[] | Date | SubstanceUomSetValue[] |
  SubstanceConversionFactor[] | SafetyLimit[];

const isNotDefined = (complexValue: CodeComplexValue<number>) => !complexValue || complexValue.value === undefined;

const isDescriptionProperty = (property: string) => comparableByDescription.indexOf(property) !== -1;

const filterDescriptionUndefineds = (property: CodeComplexValue<number>[]): CodeComplexValue<number>[] =>
  (property as CodeComplexValue<number>[]).reduce((properties, current) =>
    !isDefinedAfterTrim(current && current.description) || !current.description ? properties : [...properties, current], []);

const filterUndefineds = (property: CodeComplexValue<number>[]): CodeComplexValue<number>[] =>
  (property as CodeComplexValue<number>[]).reduce((properties, current) =>
    isNotDefined(current) ? properties : [...properties, current], []);

const mapSubstanceUomSetValues = (values: SubstanceUomSetValue[]): CodeComplexValue<number>[] => {
  return values?.map(
    value => Object.assign(
      { value: undefined },
      ...Object.keys(value).map(propertyName => {
        return {
          ...(value[propertyName] && { [propertyName]: { value: value[propertyName]?.value } })
        };
      })
    )
  );
};

const mapSubstanceConversionFactorValues = (values: SubstanceConversionFactor[]): CodeComplexValue<number>[] => {
  return values?.map(
    value => Object.assign(
      { value: undefined },
      ...Object.keys(value).map(propertyName => {
        return {
          ...(value[propertyName] && {
            [propertyName]: {
              value: isNumber(value[propertyName]) && isDefinedIncludingZero(value[propertyName])
                ? value[propertyName] : value[propertyName]?.value
            }
          })
        };
      })
    )
  );
};

const mapUomConversionFactorValues = (values: UomConversionWire[]): CodeComplexValue<number>[] => {
  return values?.map(
    value => Object.assign(
      { value: undefined },
      ...Object.keys(value).map(propertyName => {
        return {
          ...(value[propertyName] && {
            [propertyName]: {
              value: isNumber(value[propertyName]) && isDefinedIncludingZero(value[propertyName])
                ? value[propertyName] : value[propertyName]?.value
            }
          })
        };
      })
    )
  );
};

const mapSafetyLimitValues = (values: SafetyLimit[]): CodeComplexValue<number>[] => {
  return values?.map(
    value => Object.assign(
      { value: undefined },
      ...Object.keys(value).map(propertyName => {
        return {
          ...(value[propertyName] !== undefined && {
            [propertyName]: {
              value: isNumber(value[propertyName]) && isDefinedIncludingZero(value[propertyName])
                ? value[propertyName] : (value[propertyName] as any)?.value
            }
          })
        };
      })
    )
  );
};

const filterUndefinedValues = (
  property: keyof CodeItem,
  validOriginalValues: CodeComplexValue<number>[],
  originalCodeItemProperty: CodeItemPropertyValue,
  validCurrentValues: CodeComplexValue<number>[],
  codeItemProperty: CodeItemPropertyValue
) => {

  const originalCodeItemPropertyMapped = mapSubstanceUomSetValues(originalCodeItemProperty as SubstanceUomSetValue[]);
  const codeItemPropertyMapped = mapSubstanceUomSetValues(codeItemProperty as SubstanceUomSetValue[]);
  
  const originalSubstanceConversionFactorValues = mapSubstanceConversionFactorValues(
    originalCodeItemProperty as SubstanceConversionFactor[]);
  const substanceConversionFactorValues = mapSubstanceConversionFactorValues(codeItemProperty as SubstanceConversionFactor[]);

  const originalUomConversionFactorValues = mapUomConversionFactorValues(
    originalCodeItemProperty as UomConversionWire[]);
  const uomConversionFactorValues = mapUomConversionFactorValues(codeItemProperty as UomConversionWire[]);

  const originalSafetyLimitValues = mapSafetyLimitValues(originalCodeItemProperty as SafetyLimit[]);
  const safetyLimitValues = mapSafetyLimitValues(codeItemProperty as SafetyLimit[]);

  if (isDescriptionProperty(property)) {
    validOriginalValues = originalCodeItemProperty ?
      filterDescriptionUndefineds(originalCodeItemProperty as CodeComplexValue<number>[]) : [];
    validCurrentValues = codeItemProperty ? filterDescriptionUndefineds(codeItemProperty as CodeComplexValue<number>[]) : [];
  }

  else if (isWithUomSet(codeItemProperty) && !deepEquals(originalCodeItemPropertyMapped, codeItemPropertyMapped)) {
    validOriginalValues = originalCodeItemPropertyMapped;
    validCurrentValues = codeItemPropertyMapped;
  }

  else if (isWithSubstanceConversionFactor(codeItemProperty) && !deepEquals(originalSubstanceConversionFactorValues,
    substanceConversionFactorValues)) {

    validOriginalValues = originalSubstanceConversionFactorValues;
    validCurrentValues = substanceConversionFactorValues;
  }

  else if (isWithUomConversionFactor(codeItemProperty) && !deepEquals(originalUomConversionFactorValues,
    uomConversionFactorValues)) {
    validOriginalValues = originalCodeItemPropertyMapped;
    validCurrentValues = codeItemPropertyMapped;
  }

  else if (isWithSafetyLimit(codeItemProperty) && !deepEquals(originalSafetyLimitValues, safetyLimitValues)) {
    validOriginalValues = originalSafetyLimitValues;
    validCurrentValues = safetyLimitValues;
  }

  else {
    validOriginalValues = originalCodeItemProperty ? filterUndefineds(originalCodeItemProperty as CodeComplexValue<number>[]) : [];
    validCurrentValues = codeItemProperty ? filterUndefineds(codeItemProperty as CodeComplexValue<number>[]) : [];
  }

  return { validOriginalValues, validCurrentValues };
};

const hasEqualValues = (sourceProperty: CodeItemPropertyValue, comparedProperty: CodeItemPropertyValue): boolean =>
  isNotDefined(sourceProperty as CodeComplexValue<number>) && isNotDefined(comparedProperty as CodeComplexValue<number>) ||
  sourceProperty && comparedProperty &&
  (sourceProperty as CodeComplexValue<number>).value === (comparedProperty as CodeComplexValue<number>).value;

const hasEqualDescriptions = (sourceProperty: CodeComplexValue<number>, comparedProperty: CodeComplexValue<number>): boolean => {
  return isNotDefined(sourceProperty) && isNotDefined(comparedProperty) ||
    !isDefinedAfterTrim(sourceProperty && sourceProperty.description)
    && !isDefinedAfterTrim(comparedProperty && sourceProperty.description) ||
    sourceProperty && comparedProperty &&
    String(sourceProperty.description).trim() === String(comparedProperty.description).trim() &&
    sourceProperty.value === comparedProperty.value;
};

const arePropertiesEqual = (
  validOriginalValues: CodeComplexValue<number>[],
  validCurrentValues: CodeComplexValue<number>[],
  property: keyof CodeItem) => {

  if (validOriginalValues?.length !== validCurrentValues?.length) {
    return false;
  }

  const areOriginalValuesInCurrentCodeItem = validOriginalValues.every(originalValue =>
    validCurrentValues.find(currentValue => isPropertyEqual(property, originalValue, currentValue)) !== undefined
  );

  const substanceUomSetChanged = isWithUomSet(validCurrentValues) && !deepEquals(validOriginalValues, validCurrentValues);
  const substanceConversionFactorChanged = isWithSubstanceConversionFactor(validCurrentValues)
    && !deepEquals(validOriginalValues, validCurrentValues);
  const safetyLimitChanged = isWithSafetyLimit(validCurrentValues)
    && !deepEquals(validOriginalValues, validCurrentValues);

  if (!areOriginalValuesInCurrentCodeItem || !!substanceUomSetChanged || !!substanceConversionFactorChanged
    || !!safetyLimitChanged) {
    return false;
  }

  return validCurrentValues.every(currentValue =>
    validOriginalValues.find(originalValue => isPropertyEqual(property, originalValue, currentValue)) !== undefined
  );
};

const isPropertyEqual = (
  property: keyof CodeItem,
  originalCodeItemProperty: CodeItemPropertyValue,
  codeItemProperty: CodeItemPropertyValue
): boolean => {

  if (shouldCompareShallowly(originalCodeItemProperty, codeItemProperty)) {
    return String(originalCodeItemProperty).trim() === String(codeItemProperty).trim();

  } else if (shouldCompareDeeply(originalCodeItemProperty, codeItemProperty, property)) {
    return isEqual(originalCodeItemProperty, codeItemProperty);

  } else {

    if (Array.isArray(originalCodeItemProperty) || Array.isArray(codeItemProperty)) {

      let validOriginalValues: CodeComplexValue<number>[];
      let validCurrentValues: CodeComplexValue<number>[];

      ({ validOriginalValues, validCurrentValues } = filterUndefinedValues(
        property,
        validOriginalValues,
        originalCodeItemProperty,
        validCurrentValues,
        codeItemProperty)
      );

      return arePropertiesEqual(validOriginalValues, validCurrentValues, property);

    } else {

      if (isDescriptionProperty(property)) {
        return hasEqualDescriptions(originalCodeItemProperty as CodeComplexValue<number>, codeItemProperty as CodeComplexValue<number>);
      }

      else {
        return hasEqualValues(originalCodeItemProperty, codeItemProperty);
      }
    }
  }
};

const isDefinedAfterTrim = (value: string) => value !== undefined && isDefinedIncludingZero(String(value).trim());

const removeEqualPropertiesFromDiff = (
  originalCodeItem: CodeItem,
  codeItem: CodeItem,
  property: keyof CodeItem,
  diff: (keyof CodeItem)[]
) => isPropertyEqual(property, originalCodeItem[property], codeItem[property]) ? removePropertyFromDiff(diff, property) : diff;

const getObjectsDiff = (state: State) => {
  const { item: { originalCodeItem, codeItem, savingCodetableItem, deletingCodeItem } } = state;

  if (deletingCodeItem || savingCodetableItem || !originalCodeItem || !codeItem) {
    return [];
  }

  return Object.keys(originalCodeItem).reduce<(keyof CodeItem)[]>((diff, property) => {
    diff = removeUndefinedProperty(originalCodeItem, codeItem, property as keyof CodeItem, diff);
    diff = addDefinedPropertyToDiff(originalCodeItem, codeItem, property as keyof CodeItem, diff);
    diff = removeEqualPropertiesFromDiff(originalCodeItem, codeItem, property as keyof CodeItem, diff);

    return diff;
  }, removeUndefinedProperties(originalCodeItem, codeItem));
};

const isNotEmpty = (diff: string[]) => !isEqual(sortBy(diff), []);

type State = CodetableAdministrationState & TranslatedState;

export const selectors = {
  shouldPrompt: (state: State, _: any) => {
    const diff = getObjectsDiff(state);
    return isNotEmpty(diff);
  }
};
